import { ideaLoader, ideaRecordLoader, getIdeaRecordsSearch, getIdeaRecordsCount, deleteIdeaById } from './http/ideas';
import type { FetchQueryOptions, QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import type { ConcreteIdea, IdeaRecord, TopIdeas } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { ResourceQuery, ResourceQueryResponseWithMeta } from '@thinkalpha/ui-types/contracts/resource-query';
import { IdeaDTO } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import { WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { LibraryQueryResult } from 'src/models/LibraryModel';
import { invalidateSuggestionsForKey } from 'src/queries/topMenuItems';
import type { SuggestionsService } from 'src/services/SuggestionsService';
import type { ReactBindings } from 'src/types/bindings';

// Constants
export const IDEAS = 'ideas';
export const RECORDS = 'records';
export const LIBRARY = 'library';
export const COUNT = 'count';
export const TOP = 'top';
export const MODEL = 'model';
/**
 * Get Idea records, which are objects that contain the basic database metadata of the Idea.
 * Resolves to `null` if the id does not exist on the server.
 */
export const getIdeaRecordByIdQuery = (id: string): UseQueryOptions<IdeaRecord | null> => {
    return {
        queryKey: [IDEAS, RECORDS, id],
        queryFn: () => ideaRecordLoader.load(id),
    };
};

/**
 * Fetch a full idea by id. Resolves to `null` if the id does not exist on the server,
 * or if the supplied id is null/empty. Safe to use with both `useQuery` (where `enabled`
 * gates execution) and `queryClient.fetchQuery` (where it does not).
 *
 * The optional `T` type parameter narrows the plan (e.g. `getIdeaByIdQuery<ConcreteIdea & { plan: WatchlistPlan }>(id)`)
 * — the cast is the caller's promise that the stored idea has that plan shape.
 */
export const getIdeaByIdQuery = <T extends ConcreteIdea = ConcreteIdea>(
    id: string | null,
): UseQueryOptions<T | null> => {
    return {
        queryKey: [IDEAS, id],
        queryFn: () => {
            if (!id) {
                return Promise.resolve(null);
            }
            return ideaLoader.load(id) as Promise<T | null>;
        },
        enabled: Boolean(id),
    };
};

export const getIdeaDTOByIdQuery = (
    queryClient: ReactBindings['QueryClient'],
    formulaService: ReactBindings['FormulaService'],
    slugMapService: ReactBindings['SlugMapService'],
    id: string,
): FetchQueryOptions<IdeaDTO> => {
    return {
        queryKey: [IDEAS, MODEL, id],
        queryFn: async () => {
            const idea = await ideaLoader.load(id);
            if (!idea) {
                throw new Error(`Idea ${id} not found`);
            }

            switch (idea.plan.type) {
                case 'if-then':
                case undefined:
                    return IdeaDTO.fromIdeaAndPlan(
                        idea,
                        SearchPlanDTO.fromPlanAndImports(
                            idea.plan,
                            await Promise.all(
                                idea.plan.imports.map((importRef) =>
                                    IndicatorImportModel.fromIndicatorImportRef(importRef, queryClient),
                                ),
                            ),
                        ),
                    );
                case 'scanner':
                    return IdeaDTO.fromIdeaAndPlan(
                        idea,
                        await ScannerPlanDTO.fromPlan(idea.plan, queryClient, formulaService, slugMapService),
                    );
                case 'screener':
                    return IdeaDTO.fromIdeaAndPlan(
                        idea,
                        await ScreenerPlanDTO.fromPlan(idea.plan, queryClient, formulaService, slugMapService),
                    );
                case 'watchlist':
                    return IdeaDTO.fromIdeaAndPlan(idea, WatchlistPlanDTO.fromPlan(idea.plan, slugMapService));
                case 'formula':
                    throw new Error('Unimplemented conversion from formula plan');
                default:
                    throw new UnreachableCaseError(idea.plan);
            }
        },
        staleTime: 0,
        gcTime: 0,
    };
};

export function getIdeaRecordsQuery(
    resourceQuery: ResourceQuery,
    enabled = true,
): QueryObserverOptions<ResourceQueryResponseWithMeta<IdeaRecord>> {
    return {
        queryKey: [IDEAS, RECORDS, resourceQuery],
        queryFn: () => getIdeaRecordsSearch(resourceQuery),
        enabled,
    };
}

export function getIdeaRecordsForLibraryQuery(
    resourceQuery: ResourceQuery,
    enabled = true,
): QueryObserverOptions<LibraryQueryResult<IdeaRecord>> {
    return {
        queryKey: [IDEAS, RECORDS, LIBRARY, resourceQuery],
        queryFn: async () => {
            const result = await getIdeaRecordsSearch(resourceQuery).then(
                (res) => res as ResourceQueryResponseWithMeta<IdeaRecord>,
            );

            return {
                total: result.count,
                rows: result.results,
            };
        },
        enabled,
    };
}

export function removeIdeaFromRecordsQueries(idea: { id: string }, queryClient: QueryClient): void {
    queryClient.setQueriesData(
        { queryKey: [IDEAS, RECORDS, LIBRARY] },
        (data: LibraryQueryResult<IdeaRecord> | undefined): LibraryQueryResult<IdeaRecord> | undefined => {
            if (!data?.rows) return data;
            const rows = data.rows.filter((row) => row.id !== idea.id);
            const total = data.total - 1; // TODO validate we removed a record before updating the total?
            return { total, rows };
        },
    );

    queryClient.setQueriesData(
        { queryKey: [IDEAS, RECORDS] },
        (
            data: ResourceQueryResponseWithMeta<IdeaRecord> | undefined,
        ): ResourceQueryResponseWithMeta<IdeaRecord> | undefined => {
            if (!data?.results) return data;
            const rows = data.results.filter((row) => row.id !== idea.id);
            return { ...data, results: rows };
        },
    );
}

export function getIdeaCountQuery(): QueryObserverOptions<ResourceQueryResponseWithMeta<IdeaRecord>, unknown, number> {
    return {
        queryKey: [IDEAS, COUNT],
        queryFn: () => getIdeaRecordsCount(),
        select: (data) => data.count,
    };
}

export function removeIdeaFromTopIdeasQuery(idea: { id: string }, queryClient: QueryClient): void {
    queryClient.setQueriesData({ queryKey: [IDEAS, TOP] }, (data: TopIdeas | undefined): TopIdeas | undefined => {
        if (!data?.recentlyAccessed || !data?.recentlyCreated) return data;
        const recentlyAccessed = data.recentlyAccessed.filter((x) => x.id !== idea.id);
        const recentlyCreated = data.recentlyCreated.filter((x) => x.id !== idea.id);
        return { recentlyAccessed, recentlyCreated };
    });
}

export function invalidateIdeaRecordsQueries(queryClient: QueryClient): void {
    queryClient.invalidateQueries({ queryKey: [IDEAS, RECORDS] });
}

interface DeleteIdeaContext {
    previousRecordsData: [readonly unknown[], ResourceQueryResponseWithMeta<IdeaRecord> | undefined][];
    previousLibraryData: [readonly unknown[], LibraryQueryResult<IdeaRecord> | undefined][];
    previousTopIdeasData: [readonly unknown[], TopIdeas | undefined][];
}

/**
 * Get mutation options for deleting an idea.
 * Performs optimistic updates and rolls back on error.
 */
export function getDeleteIdeaMutation(
    queryClient: QueryClient,
    suggestionsService: SuggestionsService,
): UseMutationOptions<void, Error, IdeaRecord, DeleteIdeaContext> {
    return {
        mutationFn: async (idea: IdeaRecord) => {
            await deleteIdeaById(idea.id);
        },
        onMutate: async (idea) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: [IDEAS, RECORDS] });
            await queryClient.cancelQueries({ queryKey: [IDEAS, TOP] });

            // Snapshot the previous values for rollback
            const previousRecordsData = queryClient.getQueriesData<ResourceQueryResponseWithMeta<IdeaRecord>>({
                queryKey: [IDEAS, RECORDS],
            });
            const previousLibraryData = queryClient.getQueriesData<LibraryQueryResult<IdeaRecord>>({
                queryKey: [IDEAS, RECORDS, LIBRARY],
            });
            const previousTopIdeasData = queryClient.getQueriesData<TopIdeas>({
                queryKey: [IDEAS, TOP],
            });

            // Optimistically update the caches
            removeIdeaFromRecordsQueries(idea, queryClient);
            removeIdeaFromTopIdeasQuery(idea, queryClient);
            suggestionsService.delete(idea.id);

            return { previousRecordsData, previousLibraryData, previousTopIdeasData };
        },
        onError: (_error, _idea, context) => {
            // Roll back to previous values on error
            if (context) {
                for (const [queryKey, data] of context.previousRecordsData) {
                    if (data) {
                        queryClient.setQueryData(queryKey, data);
                    }
                }
                for (const [queryKey, data] of context.previousLibraryData) {
                    if (data) {
                        queryClient.setQueryData(queryKey, data);
                    }
                }
                for (const [queryKey, data] of context.previousTopIdeasData) {
                    if (data) {
                        queryClient.setQueryData(queryKey, data);
                    }
                }
                // Invalidate suggestions to refetch from localStorage
                invalidateSuggestionsForKey(queryClient);
            }
        },
        onSettled: () => {
            // Refetch after error or success to ensure we have the latest server state
            queryClient.invalidateQueries({ queryKey: [IDEAS, RECORDS] });
        },
    };
}
